import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { FileWithPath } from "react-dropzone";

import { toast } from "@/components/ui/use-toast";
import { handleApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { FIVE_MINUTES, remainingTimeToday } from "@/lib/query";
import { queryClient } from "@/lib/queryClient";
import type {
  UpdateSoftwareArchitectureReq,
  UpdateSoftwareDetailReq,
} from "@/routes/_staff/software/$id/_$id/-query/type";

import { APP_ID } from "../-constant";

import {
  activateMdmAuthentication,
  createSoftwareFromAppIntegration,
  deactivateAppIntegration,
  deleteAppIntegration,
  deleteCustomAppUser,
  getAppIntegrationContract,
  getAppIntegrationSettings,
  getAppOverviewBillingCost,
  getAppOverviewMonthlyActiveUserRate,
  getAppOverviewMonthlyCost,
  getAppOverviewRecommendedActions,
  getAppOverviewRenewalDate,
  getAppOverviewSavingCost,
  getAppOverviewUser,
  getAppOverviewUserGrowth,
  getAppUsers,
  getCountByCategories,
  getIntegratableApp,
  getMdmConfiguration,
  getPresignedUrl,
  getRefreshResourcesStatus,
  getSoftwareFromAppIntegration,
  patchExternalTenantSettings,
  postRefreshResources,
  reAuthenticate,
  reactivateAppIntegration,
  updateAppUserProvisioning,
  updateCustomApp,
  updateCustomAppContract,
  updatePricingPlan,
  updateSoftwareArchitecture,
  updateSoftwareDetail,
} from "./api";
import { appIntegrationKey } from "./key";
import { convertToUsersTableRow } from "./selector";
import type {
  AppIntegrationSettingsRes,
  CustomAppContractState,
  CustomAppState,
  GetAppIntegrationUsersParams,
  MdmActivateReq,
  PatchExternalTenantSettingsReq,
  PricingPlanState,
  ReAuthenticateReq,
  ReAuthenticateRes,
} from "./type";

export const useRefreshResourcesStatus = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.refreshStatus(id),
    queryFn: async () => getRefreshResourcesStatus(id),
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "IN_PROGRESS" ? 5000 : false,
  });
};

export const usePostRefreshResources = (id: string) => {
  const { i18n, t } = useTranslation();
  return useMutation({
    mutationFn: async () => {
      const res = await postRefreshResources(id);
      return res;
    },
    onSuccess: (data) => {
      const messages: Record<typeof data.status, string> = {
        ENQUEUED: t("pages.apps.integrations.toast.refresh.triggered"),
        ALREADY_IN_PROGRESS: t(
          "pages.apps.integrations.toast.refresh.alreadyInProgress",
        ),
        RATE_LIMITED: t("pages.apps.integrations.toast.refresh.rateLimited"),
      } as const;
      toast({ description: messages[data.status] });
      queryClient.invalidateQueries({
        queryKey: appIntegrationKey.refreshStatus(id),
      });
    },
    onError: (error) => {
      const { message } = handleApiError(error, { language: i18n.language });
      toast({ description: message, variant: "destructive" });
    },
  });
};

export const useAppData = (appId: string) => {
  return useQuery({
    queryKey: appIntegrationKey.integratableApp(appId),
    queryFn: () => {
      // NOTE: カスタムアプリの場合はAPIエラーになるため、nullを返す
      if (appId === APP_ID.CUSTOM_APP) return null;
      return getIntegratableApp(appId);
    },
    staleTime: remainingTimeToday(),
    select: (data) => data ?? null,
  });
};

export const useAppUsers = (
  id: string,
  search: GetAppIntegrationUsersParams,
) => {
  return useQuery({
    queryKey: appIntegrationKey.usersSearch(id, search),
    queryFn: () => getAppUsers(id, search),
    select: (data) => ({
      users: convertToUsersTableRow(data),
      total: data.length,
      count: data.count,
      pageIndex: data.page,
      maxPage: data.maxPage,
    }),
  });
};

export const useAppIntegrationContract = (id: string) => {
  return useSuspenseQuery({
    queryKey: appIntegrationKey.contract(id),
    queryFn: () => getAppIntegrationContract(id),
    staleTime: remainingTimeToday(),
    select: (data) => ({
      appIntegrationId: id,
      integratedApp: data.app,
      integratedTenantName: data.integratedTenantName,
      contract: data.appContract,
      isSubordinate: data.isSubordinate,
      parentApp: data.parentApp,
    }),
  });
};

export const useAppOverviewUserQuery = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.overviewUser(id),
    queryFn: () => getAppOverviewUser(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewBillingCostQuery = (id: string) => {
  return useSuspenseQuery({
    queryKey: appIntegrationKey.overviewBillingCost(id),
    queryFn: () => getAppOverviewBillingCost(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewUserGrowthQuery = (id: string) => {
  return useSuspenseQuery({
    queryKey: appIntegrationKey.overviewUserGrowth(id),
    queryFn: () => getAppOverviewUserGrowth(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewMonthlyActiveUserRateQuery = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.overviewMonthlyActiveUserRate(id),
    queryFn: () => getAppOverviewMonthlyActiveUserRate(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewMonthlyCostQuery = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.overviewMonthlyCost(id),
    queryFn: () => getAppOverviewMonthlyCost(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewRenewalDateQuery = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.overviewRenewalDate(id),
    queryFn: () => getAppOverviewRenewalDate(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewSavingCostQuery = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.overviewSavingCost(id),
    queryFn: () => getAppOverviewSavingCost(id),
    staleTime: remainingTimeToday(),
  });
};

export const useAppOverviewRecommendedActionsQuery = (id: string) => {
  return useQuery({
    queryKey: appIntegrationKey.overviewRecommendedActions(id),
    queryFn: () => getAppOverviewRecommendedActions(id),
    staleTime: remainingTimeToday(),
  });
};

export const useCountByCategories = (id: string) => {
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: appIntegrationKey.countByCategories(id),
    queryFn: () => getCountByCategories(id),
    staleTime: FIVE_MINUTES,
  });
  return { data, loading, error };
};

export const useAppIntegrationSettings = (id: string) => {
  return useSuspenseQuery({
    queryKey: appIntegrationKey.setting(id),
    queryFn: () => getAppIntegrationSettings(id),
  });
};

export const useMdmConfiguration = (appIntegrationId: string) => {
  return useQuery({
    queryKey: appIntegrationKey.mdmConfiguration(appIntegrationId),
    queryFn: () => getMdmConfiguration(appIntegrationId),
    staleTime: FIVE_MINUTES,
  });
};

export const useMdmActivateMutator = (
  appIntegrationId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n, t } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: MdmActivateReq) => {
      return activateMdmAuthentication(appIntegrationId, data);
    },
    onSuccess: (data) => {
      if (data.success) {
        invalidateAppIntegration(appIntegrationId);
        options?.onSuccess?.();
        toast({
          description: t("pages.profile.app.settings.mdm.toast.updateSuccess"),
          variant: "default",
        });
      } else {
        const errorMessage = t(
          "pages.profile.app.settings.mdm.toast.updateError",
        );
        options?.onError?.(errorMessage);
        toast({
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      const errorMessage =
        message || t("pages.profile.app.settings.mdm.toast.updateError");
      options?.onError?.(errorMessage);
      toast({ description: errorMessage, variant: "destructive" });
    },
  });

  return { mutate, isPending };
};

export const invalidateAppIntegration = (id: string) => {
  return queryClient.invalidateQueries({
    queryKey: appIntegrationKey.all(id),
  });
};

export const useTenantMutator = (id: string) => {
  const { i18n } = useTranslation();

  const { mutate } = useMutation({
    mutationFn: async (tenantData: PatchExternalTenantSettingsReq) => {
      await patchExternalTenantSettings(id, tenantData);
    },
    onSuccess: () => {
      invalidateAppIntegration(id);
      toast({ title: "Success!!" });
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ title: message });
    },
  });

  return { mutate };
};

export const useDeactivateIntegrationMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deactivateAppIntegration(id),
    onSuccess: () => {
      invalidateAppIntegration(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useDeleteIntegrationMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteAppIntegration(id),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useReactivateIntegrationMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => reactivateAppIntegration(id),
    onSuccess: () => {
      invalidateAppIntegration(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUpdateCustomAppMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (appState: CustomAppState) => {
      return updateCustomApp(id, appState);
    },
    onSuccess: () => {
      invalidateAppIntegration(id);
      // NOTE:
      // Custom SaaSのロゴは /software/registry 側の一覧でも表示される。
      // そちらは appIntegrationKey とは別の queryKey を使っているため、
      // ここで追加で invalidate して更新後のロゴ（timestamp query付きURL）を反映させる。
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "integratedApps" ||
          query.queryKey[0] === "allIntegrationApps",
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUploadImageMutator = (options?: {
  onSuccess?: (data: { key: string }) => void;
  onError?: (message: string) => void;
}) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: async (file: FileWithPath) => {
      const data = await getPresignedUrl(file.name);
      if (data) {
        await fetch(data.url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });
        return data;
      }
      throw new Error("Failed to get presigned URL");
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUpdateCustomAppContractMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (body: CustomAppContractState) => {
      return updateCustomAppContract(id, body);
    },
    onSuccess: () => {
      invalidateAppIntegration(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const usePricingPlanMutator = (
  appIntegrationId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate } = useMutation({
    mutationFn: (pricingData: PricingPlanState) => {
      return updatePricingPlan(appIntegrationId, pricingData);
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate };
};

export const useAppUserProvisioningMutator = (
  id: string,
  options?: {
    isEnabled: boolean;
    onSuccess?: (data: AppIntegrationSettingsRes) => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate } = useMutation({
    mutationFn: () => {
      return updateAppUserProvisioning(id, {
        USER_PROVISIONING: options?.isEnabled ?? false,
      });
    },
    onSuccess: (data) => {
      invalidateAppIntegration(id);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate };
};

export const useReAuthenticateMutator = (
  appIntegrationId: string,
  options?: {
    onSuccess?: (data: ReAuthenticateRes) => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ReAuthenticateReq) => {
      return reAuthenticate(appIntegrationId, data);
    },
    onSuccess: (data) => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useDeleteAppUserMutator = (
  appIntegrationId: string,
  appUserId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteCustomAppUser(appIntegrationId, appUserId),
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

// Software data hooks
export const useSoftwareFromAppIntegration = (appIntegrationId: string) => {
  return useQuery({
    queryKey: appIntegrationKey.software(appIntegrationId),
    queryFn: async () => {
      // TODO: createをuseQueryの中でするのは良くないので、useQueryから分離する
      // ただし、エラーハンドリングをうまくやらないとエラーモーダルが出てしまうので頑張る
      try {
        // 1. まずGETで取得を試す
        return await getSoftwareFromAppIntegration(appIntegrationId);
      } catch {
        // 2. 取得できなかった場合、POSTで作成してそのレスポンスを使用
        return await createSoftwareFromAppIntegration(appIntegrationId);
      }
    },
    staleTime: remainingTimeToday(),
  });
};

// Software mutation hooks (using existing software API)
export const useUpdateSoftwareDetailMutator = (
  softwareId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software",
  });

  return useMutation({
    mutationFn: (data: UpdateSoftwareDetailReq) =>
      updateSoftwareDetail(softwareId, data),
    onSuccess: () => {
      // ソフトウェアとアプリ統合の両方のキャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: ["software", softwareId],
      });
      queryClient.invalidateQueries({
        queryKey: appIntegrationKey.software(softwareId), // app-integrationのIDとsoftwareのIDが同じ場合
      });
      toast({ description: t("edit.toast.updateSuccess") });
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ description: message, variant: "destructive" });
      options?.onError?.(message);
    },
  });
};

export const useUpdateSoftwareArchitectureMutator = (
  softwareId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software",
  });

  return useMutation({
    mutationFn: (data: UpdateSoftwareArchitectureReq) =>
      updateSoftwareArchitecture(softwareId, data),
    onSuccess: () => {
      // ソフトウェアとアプリ統合の両方のキャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: ["software", softwareId],
      });
      queryClient.invalidateQueries({
        queryKey: appIntegrationKey.software(softwareId),
      });
      toast({ description: t("edit.toast.updateSuccess") });
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ description: message, variant: "destructive" });
      options?.onError?.(message);
    },
  });
};
